/* ============================================================================
 * charger-test-core.js — how many watts a charger is really delivering.
 *
 * No DOM, UMD, so the same file runs in the page and in node.
 *
 * WHAT A BROWSER ACTUALLY KNOWS. Nothing about volts or amps. All it has is the
 * battery percentage, usually in whole steps, and whether the device is
 * plugged in. Everything here is built from the TIMES AT WHICH THAT PERCENTAGE
 * CHANGES, which is a far better measurement than it sounds: a step is a
 * crossing of a known level, so each one is an exact point on the charge curve
 * rather than a noisy sample of it.
 *
 * THE TRAP THAT MAKES NAIVE READINGS WRONG. A phone charges and discharges at
 * the same time. With the screen on it can be spending two to four watts while
 * the charger fills it, so the rate the battery gains is the charger's output
 * MINUS the phone's own appetite. A tool that reports the gain as "charger
 * power" understates every charger, and understates the good ones most, because
 * a fast charger finishes sooner and spends less of its energy on the screen.
 *
 * So this measures BOTH: the fall while unplugged and the rise while plugged,
 * at the same screen brightness. Their sum is what the charger delivers. That
 * is the whole idea, and it is why the tool asks for two runs instead of one.
 *
 * THE OTHER HONEST LIMIT. Lithium cells charge at constant current up to around
 * eighty per cent and then taper. A run measured at 85% will read low no matter
 * how good the charger is, so every result carries the state of charge it was
 * taken at, and the tool says when a comparison is being made across different
 * parts of the curve.
 * ==========================================================================*/
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.ChargerTestCore = factory();
}(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    /* The reported level is quantised. A transition to a new value happens when
       the true level crosses the boundary half a step below it, so each
       transition is an exact point on the curve — which is what makes a fit to
       a handful of steps better than a long average of a noisy signal. */
    function transitions(samples) {
        var out = [], i;
        for (i = 1; i < samples.length; i++) {
            if (samples[i].level !== samples[i - 1].level) {
                out.push({
                    t: samples[i].t,
                    level: samples[i].level,
                    from: samples[i - 1].level,
                    rising: samples[i].level > samples[i - 1].level,
                });
            }
        }
        return out;
    }

    /* Step size, measured rather than assumed: most platforms report whole
       percent, some report finer, and one that reports 5% steps would make a
       short test meaningless. */
    function stepSize(trans) {
        if (trans.length < 2) return null;
        var gaps = [], i;
        for (i = 0; i < trans.length; i++) {
            var d = Math.abs(trans[i].level - trans[i].from);
            if (d > 0) gaps.push(d);
        }
        gaps.sort(function (a, b) { return a - b; });
        return gaps.length ? gaps[Math.floor(gaps.length / 2)] : null;
    }

    /**
     * Rate of change in percent per hour, from the transition times.
     * Returns null when there are too few transitions to say anything, which is
     * the common case for a slow charger and a short test — and saying nothing
     * is the right answer there.
     */
    function rate(samples, opt) {
        opt = opt || {};
        var tr = transitions(samples);
        var minTrans = opt.minTransitions || 3;
        if (tr.length < minTrans) {
            return { ok: false, reason: 'too-few-steps', transitions: tr.length, step: stepSize(tr) };
        }
        var step = stepSize(tr) || 1;
        /* Each transition to value v is the moment the true level crossed
           v - step/2 when rising, or v + step/2 when falling. */
        var xs = [], ys = [], i;
        for (i = 0; i < tr.length; i++) {
            xs.push(tr[i].t);
            ys.push(tr[i].level + (tr[i].rising ? -step / 2 : step / 2));
        }
        var n = xs.length, sx = 0, sy = 0, sxx = 0, sxy = 0;
        for (i = 0; i < n; i++) { sx += xs[i]; sy += ys[i]; sxx += xs[i] * xs[i]; sxy += xs[i] * ys[i]; }
        var den = n * sxx - sx * sx;
        if (Math.abs(den) < 1e-12) return { ok: false, reason: 'no-span', transitions: n };
        var slope = (n * sxy - sx * sy) / den;      /* percent per second */
        var inter = (sy - slope * sx) / n;
        var res = 0;
        for (i = 0; i < n; i++) { var e = ys[i] - (slope * xs[i] + inter); res += e * e; }
        var rms = Math.sqrt(res / Math.max(1, n - 2));
        /* Standard error of the slope, which is what the uncertainty of the
           whole measurement rests on. */
        var se = rms / Math.sqrt(sxx - sx * sx / n);
        return {
            ok: true,
            pctPerHour: slope * 3600,
            sePctPerHour: se * 3600,
            transitions: n,
            step: step,
            spanSec: xs[n - 1] - xs[0],
            fromLevel: Math.min.apply(null, ys),
            toLevel: Math.max.apply(null, ys),
            midLevel: (Math.min.apply(null, ys) + Math.max.apply(null, ys)) / 2,
        };
    }

    /* Battery capacity in watt-hours. A phone's label gives milliamp-hours, and
       the nominal cell voltage is 3.85 V for every modern lithium-polymer pack;
       using 3.7 V (the older figure) understates every result by 4%. */
    function capacityWh(mAh, nominalV) {
        if (!(mAh > 0)) return null;
        return mAh * (nominalV || 3.85) / 1000;
    }

    /**
     * What the charger is really delivering.
     *   chargeRate     - percent per hour while plugged in (positive)
     *   dischargeRate  - percent per hour while unplugged (negative or its size)
     *   wh             - battery capacity in watt-hours
     * Without the discharge run the answer is the NET gain, and is labelled so.
     */
    function chargerWatts(chargeRate, dischargeRate, wh) {
        if (!(wh > 0) || chargeRate == null) return null;
        var gain = chargeRate;                              /* %/h into the battery */
        var selfUse = dischargeRate == null ? null : Math.abs(dischargeRate);
        var netW = gain / 100 * wh;
        if (selfUse == null) {
            return { netW: netW, deliveredW: null, selfW: null, complete: false };
        }
        var selfW = selfUse / 100 * wh;
        return { netW: netW, selfW: selfW, deliveredW: netW + selfW, complete: true };
    }

    /* Time to full from here, using the rate just measured. Beyond about 80%
       the current tapers, so an estimate that crosses that line is flagged
       rather than quietly extrapolated. */
    function timeToFull(levelPct, pctPerHour) {
        if (!(pctPerHour > 0)) return null;
        var hours = (100 - levelPct) / pctPerHour;
        return {
            hours: hours,
            crossesTaper: levelPct < 80,
            /* The constant-current part ends near 80%; the rest is slower by
               roughly a factor of two on most phones. */
            realisticHours: levelPct >= 80 ? hours : (80 - levelPct) / pctPerHour + 20 / (pctPerHour / 2),
        };
    }

    /* Whether a run is worth comparing with another. Two runs taken in
       different parts of the charge curve are not measurements of two chargers,
       they are measurements of two states of one battery. */
    function comparable(a, b, opt) {
        opt = opt || {};
        var maxGap = opt.maxLevelGap || 15;
        if (a == null || b == null) return { ok: false, reason: 'missing' };
        if (Math.max(a.midLevel, b.midLevel) > (opt.taperAt || 80)) {
            return { ok: false, reason: 'above-taper' };
        }
        if (Math.abs(a.midLevel - b.midLevel) > maxGap) {
            return { ok: false, reason: 'different-charge-state', gap: Math.abs(a.midLevel - b.midLevel) };
        }
        return { ok: true };
    }

    /* How long a test still needs, from the rate so far — so the page can say
       "about four more minutes" instead of spinning silently. */
    function etaSeconds(currentRate, step, haveTransitions, wantTransitions) {
        if (!(Math.abs(currentRate) > 0)) return null;
        var need = Math.max(0, (wantTransitions || 5) - (haveTransitions || 0));
        var perStep = (step || 1) / Math.abs(currentRate) * 3600;
        return need * perStep;
    }

    return {
        transitions: transitions,
        stepSize: stepSize,
        rate: rate,
        capacityWh: capacityWh,
        chargerWatts: chargerWatts,
        timeToFull: timeToFull,
        comparable: comparable,
        etaSeconds: etaSeconds,
    };
}));
